import difference from "lodash/difference";
import {
  Issue,
  IssueAttributes,
  IssueCustomAttribute,
  PlacementType,
  ProjectMemberType,
} from "@adsk/acc-issues-types";
import { trackEvent } from "@adsk/acc-issues-core/utils/analytics/trackEvent";
import {
  IssueCreateClickAnalyticsEvent,
  IssueCreateMarkupClickAnalyticsEvent,
  IssueCreateMarkupTypeClickAnalyticsEvent,
  IssueCreateMarkupTypeSearchAnalyticsEvent,
  IssueCreateMarkupTypeClearSearchAnalyticsEvent,
  IssueCreateCancelAnalyticsEvent,
  IssueEditSaveAnalyticsEvent,
  IssueReferenceAddAnalyticsEvent,
  IssueReferenceRemoveAnalyticsEvent,
  EditableIssueFields,
  BASE_ISSUE_ATTRIBUTE_TO_ANALYTICS_FIELD,
  IssueCreateEditFieldAnalyticsEvent,
  IssueCreateCancelConfirmationAnalyticsEvent,
  IssueAddCommentsAnalyticsEvent,
  IssueRemoveIssueAnnotationSuccessAnalyticsEvent,
  BaseIssueAttributeToAnalyticsFieldKey,
  IssueThumbnailOpenAnalyticsEvent,
  IssueThumbnailOpenFromActivityLogAnalyticsEvent,
  IssueThumbnailRetakeAnalyticsEvent,
  IssueThumbnailAnnotateClickAnalyticsEvent,
  IssueSnapshotNavigateAwayAnalyticsEvent,
  IssueSnapshotCreateAnalyticsEvent,
  IssueSnapshotStatusAnalyticsEvent,
  IssueSnapshotCreateTrigger,
  IssueAttachmentAddAnalyticsEvent,
  IssueAttachmentRemoveAnalyticsEvent,
  IssueAttachmentClickAnalyticsEvent,
  IssueWatcherSelect,
  IssueWatcherDeselect,
} from "./analyticsLib093IssuesEvents";
import { IssueEditBulkEditUnpublishEvent } from "./issueEdit/issueEditBulkEditUnpublishEvent";
import { IssueEditBulkEditPublishEvent } from "./issueEdit/issueEditBulkEditPublishEvent";
import { IssueEditBulkEditClickEvent } from "./issueEdit/issueEditBulkEditClickEvent";
import { IssueEditSelectEvent } from "./issueEdit/issueEditSelectEvent";
import { IssueEditSelectAllEvent } from "./issueEdit/issueEditSelectAllEvent";
import { MarkupCreationPayload } from "@adsk/acc-issues-types";
import { Company, Role, User } from "@adsk/alloy-provider";
import { getCommentsMentionAnalytics } from "./utils/getCommentsMentionAnalytics";
import { WatcherObject } from "@adsk/acc-issues-types";

export const trackIssueCreateClick = () => {
  trackEvent(new IssueCreateClickAnalyticsEvent());
};

export const trackIssueCreateMarkupClick = () => {
  trackEvent(new IssueCreateMarkupClickAnalyticsEvent());
};

export const trackIssueCreateMarkupTypeClick = () => {
  trackEvent(new IssueCreateMarkupTypeClickAnalyticsEvent());
};

export const trackIssueCreateMarkupTypeSearch = () => {
  trackEvent(new IssueCreateMarkupTypeSearchAnalyticsEvent());
};

export const trackIssueCreateMarkupTypeClearSearch = () => {
  trackEvent(new IssueCreateMarkupTypeClearSearchAnalyticsEvent());
};

export const trackIssueThumbnailOpen = (placementType: PlacementType) => {
  trackEvent(new IssueThumbnailOpenAnalyticsEvent({ placementType }));
};

export const trackIssueThumbnailOpenFromActivityPanel = (
  placementType: PlacementType
) => {
  trackEvent(
    new IssueThumbnailOpenFromActivityLogAnalyticsEvent({
      placementType,
    })
  );
};

export const trackIssueThumbnailRetake = (keepOldThumbnail: boolean) => {
  trackEvent(new IssueThumbnailRetakeAnalyticsEvent({ keepOldThumbnail }));
};

export const trackIssueThumbnailAnnotateClick = (issueId: string) => {
  trackEvent(new IssueThumbnailAnnotateClickAnalyticsEvent({ issueId }));
};

export const trackIssueSnapshotNavigateAway = (
  placementType: PlacementType
) => {
  trackEvent(
    new IssueSnapshotNavigateAwayAnalyticsEvent({
      placementType,
    })
  );
};

export const trackIssueSnapshotCreate = ({
  issueId,
  trigger,
  placementType,
}: {
  issueId: string;
  trigger: IssueSnapshotCreateTrigger;
  placementType: PlacementType;
}) => {
  trackEvent(
    new IssueSnapshotCreateAnalyticsEvent({
      issueId,
      trigger,
      placementType,
    })
  );
};

export const trackIssueSnapshotStatus = ({
  issueId,
  hasMarkups,
  hres,
  vres,
  placementType,
}: {
  issueId: string;
  hasMarkups: boolean;
  hres: number;
  vres: number;
  placementType: PlacementType;
}) => {
  trackEvent(
    new IssueSnapshotStatusAnalyticsEvent({
      issueId,
      hasMarkups,
      hres,
      vres,
      placementType,
    })
  );
};

export const trackIssueCreateCancel = () => {
  trackEvent(new IssueCreateCancelAnalyticsEvent());
};

//currently not being used since it reqires some modification to the CreationModal
export const trackIssueCreateCancelConfirmation = (action: string) => {
  trackEvent(
    new IssueCreateCancelConfirmationAnalyticsEvent({
      issue_create_cancel_confirmation_action: action,
    })
  );
};

export const trackIssueCreateEditField = (fieldTypes: string[]) => {
  trackEvent(
    new IssueCreateEditFieldAnalyticsEvent({ field_type: fieldTypes })
  );
};

export enum UpdateOrigin {
  INLINE_TABLE = "inline_table",
  DETAIL_PANEL = "detail_panel",
}

// name: "issue.edit.save"
// description: "An issue is edited"
// -- this should only be used for normal fields, for custom fields use trackIssueCustomFieldEdit
export const trackIssueBaseFieldEdit = ({
  issue,
  issueChanges,
  analyticsData,
}: {
  issue: Issue;
  issueChanges?: Partial<IssueAttributes>;
  analyticsData?: {
    placements?: MarkupCreationPayload | null;
    currentWatcherUsersCount?: number;
    previousWatcherUsersCount?: number;
    origin?: UpdateOrigin;
  };
}) => {
  const { origin, ...restAnalyticsData } = analyticsData || {};
  Object.entries({ ...issueChanges, ...restAnalyticsData } as any).forEach(
    ([key, value]) => {
      const analyticsField =
        BASE_ISSUE_ATTRIBUTE_TO_ANALYTICS_FIELD[
          key as BaseIssueAttributeToAnalyticsFieldKey
        ];
      if (analyticsField) {
        trackEvent(
          new IssueEditSaveAnalyticsEvent({
            issueUid: issue.id,
            field: analyticsField,
            // coerce these to strings for typescript, since they can be numbers too
            oldFieldValue: "" + (issue[key as keyof Issue] || ""),
            editedFieldValue: "" + (value || ""),
            editSource: origin,
          })
        );
      }
      if (key === "watchers" || key === "watcherObjects") {
        let beforeUserIds: string[] = [];
        let afterUserIds: string[] = [];
        if (key === "watchers") {
          beforeUserIds = issue.watchers || [];
          afterUserIds = value as string[];
        }
        if (key === "watcherObjects") {
          beforeUserIds = issue.watcherObjects?.map(x => x.id) || [];
          afterUserIds = (value as WatcherObject[]).map(x => x.id) as string[];
        }
        const currentWatcherUsersCount =
          restAnalyticsData?.currentWatcherUsersCount || 0;
        const previousWatcherUsersCount =
          restAnalyticsData?.previousWatcherUsersCount || 0;

        const watchersAdded = difference(afterUserIds, beforeUserIds);
        const watchersRemoved = difference(beforeUserIds, afterUserIds);

        const watcherType =
          key === "watcherObjects" &&
          issueChanges &&
          issueChanges.watcherObjects
            ? issueChanges.watcherObjects.map(x => x.type)
            : undefined;
        const watchersCountAdded =
          currentWatcherUsersCount - previousWatcherUsersCount;
        const watchersCountRemoved =
          previousWatcherUsersCount - currentWatcherUsersCount;
        if (watchersAdded.length) {
          trackEvent(
            new IssueEditSaveAnalyticsEvent({
              issueUid: issue.id,
              watcherType,
              field: "watcher_added",
              // coerce these to strings for typescript, since they are arrays
              watcherCt:
                key === "watcherObjects"
                  ? String(watchersCountAdded)
                  : "" + watchersAdded.length,
              oldFieldValue: "" + beforeUserIds.length,
              editedFieldValue: "" + afterUserIds.length,
            })
          );
        }
        if (watchersRemoved.length) {
          trackEvent(
            new IssueEditSaveAnalyticsEvent({
              issueUid: issue.id,
              watcherType,
              field: "watcher_removed",
              // coerce these to strings for typescript, since they are arrays
              watcherCt:
                key === "watcherObjects"
                  ? String(watchersCountRemoved)
                  : "" + watchersRemoved.length,
              oldFieldValue: "" + beforeUserIds.length,
              editedFieldValue: afterUserIds
                ? String(afterUserIds.length)
                : "0",
            })
          );
        }
      }
    }
  );
};

export const trackIssueCustomFieldEdit = ({
  issueUid,
  issueAttribute,
  oldValue,
  newValue,
}: {
  issueUid: string;
  issueAttribute: IssueCustomAttribute | undefined;
  oldValue: any;
  newValue: any;
}) => {
  if (!issueAttribute) {
    return;
  }
  trackEvent(
    new IssueEditSaveAnalyticsEvent({
      issueUid: issueUid,
      field: `cf_${issueAttribute.type}_type` as EditableIssueFields,
      oldFieldValue: oldValue || "",
      editedFieldValue: newValue || "",
      customFieldName: issueAttribute.title,
    })
  );
};

//  name: "issue.reference.add"
//  description: "When a reference between an issue and another object is created"
export const trackIssueReferenceAdd = ({
  issueUid,
  objectUid,
  objectType,
}: ConstructorParameters<typeof IssueReferenceAddAnalyticsEvent>[0]) => {
  trackEvent(
    new IssueReferenceAddAnalyticsEvent({
      issueUid,
      objectUid,
      objectType,
    })
  );
};

//   name: "issue.reference.remove"
//   description: "When a reference between an issue and another object is created"
export const trackIssueReferenceRemove = ({
  issueUid,
  objectUid,
  objectType,
}: ConstructorParameters<typeof IssueReferenceRemoveAnalyticsEvent>[0]) => {
  trackEvent(
    new IssueReferenceRemoveAnalyticsEvent({
      issueUid,
      objectUid,
      objectType,
    })
  );
};

export const trackIssueAttachmentAdd = ({
  attachmentFileType,
  issueId,
}: ConstructorParameters<typeof IssueAttachmentAddAnalyticsEvent>[0]) => {
  trackEvent(
    new IssueAttachmentAddAnalyticsEvent({ attachmentFileType, issueId })
  );
};

export const trackIssueAttachmentRemove = ({
  attachmentFileType,
  issueId,
}: ConstructorParameters<typeof IssueAttachmentRemoveAnalyticsEvent>[0]) => {
  trackEvent(
    new IssueAttachmentRemoveAnalyticsEvent({ attachmentFileType, issueId })
  );
};

export const trackIssueAttachmentClick = ({
  attachmentFileType,
  issueId,
}: ConstructorParameters<typeof IssueAttachmentClickAnalyticsEvent>[0]) => {
  trackEvent(
    new IssueAttachmentClickAnalyticsEvent({ attachmentFileType, issueId })
  );
};

export const trackIssueBulkEditSuccess = ({
  selectedCount,
  changedFields,
}: {
  selectedCount: number;
  changedFields: any;
}) => {
  // @TODO when the infra is fixed we can consume the latest version of
  // PG's analytics lib in the adsk context, create this event in that library and use it
  trackEvent({
    name: "issue.edit.bulk_edit.success",
    parameters: {
      selected_count: selectedCount,
      changedFields,
    },
  });
};

export const trackIssueBulkEditPartialFailure = ({
  successCount,
  failureCount,
}: {
  successCount: number;
  failureCount: number;
}) => {
  // @TODO when the infra is fixed we can consume the latest version of
  // PG's analytics lib in the adsk context, create this event in that library and use it
  trackEvent({
    name: "issue.edit.bulk_edit.partial_failure",
    parameters: {
      success_count: successCount,
      failure_count: failureCount,
    },
  });
};

export const trackIssueBulkEditFailure = ({
  selectedCount,
}: {
  selectedCount: number;
}) => {
  // @TODO when the infra is fixed we can consume the latest version of
  // PG's analytics lib in the adsk context, create this event in that library and use it
  trackEvent({
    name: "issue.edit.bulk_edit.fail",
    parameters: {
      selected_count: selectedCount,
    },
  });
};

export const trackBulkEditUnpublishAction = ({
  selectedCount,
}: {
  selectedCount: number;
}) => {
  trackEvent(new IssueEditBulkEditUnpublishEvent({ selectedCount }));
};

export const trackBulkEditPublishAction = ({
  selectedCount,
}: {
  selectedCount: number;
}) => {
  trackEvent(new IssueEditBulkEditPublishEvent({ selectedCount }));
};

export const trackBulkEditClickAction = () => {
  trackEvent(new IssueEditBulkEditClickEvent());
};

export const trackEditSelect = () => {
  trackEvent(new IssueEditSelectEvent());
};

export const trackEditSelectAll = () => {
  trackEvent(new IssueEditSelectAllEvent());
};

export const trackIssueAddComment = ({
  comment,
  users,
  rolesList,
  companiesList,
}: {
  comment: string;
  users: User[];
  rolesList: Role[];
  companiesList: Company[];
}) => {
  const numOfCharacters = comment.length;
  const mentionsAnalyticsData = getCommentsMentionAnalytics({
    comment,
    users,
    rolesList,
    companiesList,
  });

  trackEvent(
    new IssueAddCommentsAnalyticsEvent({
      commentCharacters: numOfCharacters,
      ...mentionsAnalyticsData,
    })
  );
};

export const trackRemoveIssueAnnotationSuccess = ({
  issue_uid,
  sheet_uid,
}: {
  issue_uid: string;
  sheet_uid?: string;
}) => {
  trackEvent(
    new IssueRemoveIssueAnnotationSuccessAnalyticsEvent({
      issue_uid,
      sheet_uid,
    })
  );
};

export {
  trackQRPReportConfirm,
  trackQRPReportCompleted,
} from "@adsk/acc-issues-common/utils/analytics/qrpReportTracking";

export const trackWatcherSelected = (params: {
  issueId: string;
  watcherType: ProjectMemberType;
}) => {
  trackEvent(new IssueWatcherSelect(params));
};

export const trackWatcherDeselected = (params: {
  issueId: string;
  watcherType: ProjectMemberType;
}) => {
  trackEvent(new IssueWatcherDeselect(params));
};
